import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  KeyboardArrowDown as MenuIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { ReactComponent as PersonIcon } from '../assets/person-outline.svg';
import cobalt_logo from '../cobalt_logo.svg';
import { formatBusinessName } from '../utils/nameFormatters';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  showBackButton?: boolean;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  title = 'Businesses',
  showBackButton = false,
}) => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);

	const handleBack = () => {
		// location.key is 'default' when there's no prior in-app history
		// (direct link / new tab), so fall back to the list view.
		if (location.key === 'default') {
			navigate('/verifications');
		} else {
			navigate(-1);
		}
	};

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    handleMenuClose();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#ffffff',
      }}
    >
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #D9DDF0',
          height: '56px',
          mb: 2,
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ height: '100%', minHeight: '56px !important' }}>
          {showBackButton && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'sticky',
                zIndex: 1000,
                backgroundColor: '#ffffff',
                mr: 1,
              }}
            >
              <IconButton onClick={handleBack} size="small">
                <ArrowBackIcon
                  fontSize="small"
                  sx={{ color: '#B2B8CF', fontWeight: 100 }}
                />
              </IconButton>
            </Box>
          )}
          {title && title !== 'Businesses' ? (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 600 }}>Businesses</Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  color: '#676E8B',
                  fontSize: '14px',
                  ml: -1,
                  mb: -0.25,
                }}
              >
                {`/ ${formatBusinessName(
                  title.replace('Business Details / ', '')
                )}`}
              </Typography>
            </Box>
          ) : (
            <img
              src={cobalt_logo}
              alt="Cobalt Logo"
              style={{ width: '100px', marginRight: '20px', height: '24px' }}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />

          {isAuthenticated ? (
            <>
              <PersonIcon style={{ fontSize: 32, color: '#2962ff' }} />
              <Typography variant="body2" ml={1}>
                {(user as any)?.name || (user as any)?.email || 'User'}
              </Typography>
              <IconButton
                size="small"
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMenuClick}
              >
                <MenuIcon sx={{ color: '#B2B8CF' }} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={() => {
                    navigate('/verifications');
                    handleMenuClose();
                  }}
                >
                  Verifications
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate('/profile');
                    handleMenuClose();
                  }}
                >
                  Profile
                </MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </>
          ) : (
            <Button color="inherit" onClick={() => navigate('/login')}>
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flex: 1,
          backgroundColor: '#ffffff',
          overflow: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
